// Seuil calorique quotidien selon la phase (déficit / maintenance / surplus).
import { clamp, round } from './statistics';
import type { Profile, Sex, TrainingPhase } from './types';

// ~7700 kcal ≈ 1 kg de masse grasse (Wishnofsky 1958).
export const KCAL_PER_KG_FAT = 7700;

// Plancher de sécurité : jamais de seuil sous ces valeurs (anti sous-nutrition).
export const SAFE_FLOOR_KCAL = { male: 1500, female: 1200 } as const;

// Plafond de surplus en prise de masse : le rythme de prise n'étant pas sourçable
// (le muscle n'est pas 7700 kcal/kg), on borne le surplus réglable à +1000 kcal/j.
export const MAX_SURPLUS_KCAL = 1000;

export interface ThresholdSuggestion {
  suggested: number;
  effectiveDeficit: number;
  weeklyLossKg: number;
  /** true si le plancher de sécurité a tronqué le déficit demandé. */
  flooredBySafety: boolean;
}

export function suggestThreshold(
  tdeeKcal: number,
  weeklyLossKg: number,
  sex: 'male' | 'female',
): ThresholdSuggestion {
  const requestedDeficit = (weeklyLossKg * KCAL_PER_KG_FAT) / 7;
  const rawThreshold = tdeeKcal - requestedDeficit;
  const floor = SAFE_FLOOR_KCAL[sex];
  const suggested = round(clamp(rawThreshold, floor, tdeeKcal));
  const effectiveDeficit = round(tdeeKcal - suggested);
  return {
    suggested,
    effectiveDeficit,
    weeklyLossKg: round((effectiveDeficit * 7) / KCAL_PER_KG_FAT, 3),
    flooredBySafety: rawThreshold < floor,
  };
}

// Résultat du seuil calorique selon la phase (union discriminée : chaque phase ne
// porte que ses propres champs de libellé).
export type CalorieTargetResult =
  | { phase: 'cut'; threshold: number; suggestion: ThresholdSuggestion }
  | { phase: 'bulk'; threshold: number; surplusKcal: number; cappedBySafety: boolean }
  | { phase: 'recovery'; threshold: number };

/**
 * Seuil calorique quotidien selon la phase d'entraînement :
 * - sèche → déficit (délègue à `suggestThreshold`, comportement inchangé) ;
 * - récupération → maintenance = TDEE (Tipton 2015 : viser l'équilibre énergétique) ;
 * - prise de masse → TDEE + surplus borné (bande sourcée, plafond `MAX_SURPLUS_KCAL`).
 */
export function resolveCalorieTarget(
  profile: Pick<Profile, 'trainingPhase' | 'weeklyLossKg' | 'surplusKcal'>,
  tdeeKcal: number,
  sex: Sex,
): CalorieTargetResult {
  const phase: TrainingPhase = profile.trainingPhase;
  if (phase === 'bulk') {
    const surplusKcal = clamp(profile.surplusKcal, 0, MAX_SURPLUS_KCAL);
    return {
      phase: 'bulk',
      threshold: round(tdeeKcal + surplusKcal),
      surplusKcal,
      cappedBySafety: profile.surplusKcal > MAX_SURPLUS_KCAL,
    };
  }
  if (phase === 'recovery') {
    return { phase: 'recovery', threshold: round(tdeeKcal) };
  }
  const suggestion = suggestThreshold(tdeeKcal, profile.weeklyLossKg, sex);
  return { phase: 'cut', threshold: suggestion.suggested, suggestion };
}
